import { apply, burst, celebrate, onceForever, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { MAPS } from '../world/map';
import { riding } from './places';
import { stamp } from './passport';
import { registerObjects, registerTalk } from './registry';
import { order, quiz, travelTo } from './trips';

/**
 * Content of the kaohsiung package: the KMRT and the light rail move you around the map, the landmarks
 * teach a fact and ask one question in English, the stalls sell papaya milk, oyster omelettes, grilled
 * squid and Cijin ice cream, and the Love Boat takes you down the river.
 */
const kh = () => m().kaohsiung;
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const MARKET_OPEN = 17 * 60;
const marketOpen = () => S().time >= MARKET_OPEN;

/** A fact with one action button; `onAct` runs the reward and returns the follow-up line. */
function landmark(id: string, icon: string, text: string, act: string, onAct: () => string, en?: string): void {
  if (en) sayEnglish(en, 0.85);
  say({
    who: objName(id),
    icon,
    text,
    en,
    choices: [{ text: act, run: () => say({ who: objName(id), icon, text: onAct(), en }) }, { text: m().common.back }],
  });
}

/** A fact that leads into a `quiz`; stars once ever, a smile once a day. */
function factQuiz(id: string, icon: string, text: string, act: string, t: { q: string; a: readonly string[]; right: string; wrong: string }, after?: () => void): void {
  say({
    who: objName(id),
    icon,
    text,
    choices: [
      {
        text: act,
        run: () =>
          quiz(objName(id), icon, text, t.q, t.a, t.right, t.wrong, () => {
            if (onceForever(`kh.quiz.${id}`)) apply({ stars: 3, study: 3 });
            else if (onceToday(`kh.quiz.${id}`)) apply({ mood: 4 });
            after?.();
          }),
      },
      { text: m().common.back },
    ],
  });
}

// --- Getting around: KMRT red/orange lines and the circular light rail -------------------------------

type KmrtStop = 'zuoying' | 'formosa' | 'yancheng';
const KMRT_SPOT: Record<KmrtStop, string> = { zuoying: 'train', formosa: 'formosa', yancheng: 'yancheng' };

function kmrt(here: KmrtStop): void {
  const t = kh().kmrt;
  sayEnglish(t.ask, 0.9);
  say({
    who: t.name,
    icon: '🚇',
    text: `“${t.ask}”\n\n${t.text}`,
    en: t.ask,
    choices: [
      ...(['zuoying', 'formosa', 'yancheng'] as KmrtStop[])
        .filter((k) => k !== here)
        .map((k): Choice => ({
          text: t.to[k],
          cost: m().common.minutes(12),
          run: () => {
            if (riding.on === 'bike') riding.on = null;
            sfx('door');
            apply({ minutes: 12 }, { float: false });
            stamp('kmrt');
            travelTo('kaohsiung', MAPS.kaohsiung.spots![KMRT_SPOT[k]]);
            window.setTimeout(() => toast(t.riding), 300);
          },
        })),
      { text: m().common.back },
    ],
  });
}

function lrt(here: 'pier2' | 'hamasen'): void {
  const t = kh().lrt;
  const other = here === 'pier2' ? 'hamasen' : 'pier2';
  say({
    who: objName(here === 'pier2' ? 'kh_lrt' : 'kh_lrtHamasen'),
    icon: '🚈',
    text: t.text,
    choices: [
      {
        text: t.to[other],
        cost: m().common.minutes(6),
        run: () => {
          if (riding.on === 'bike') riding.on = null;
          sfx('door');
          apply({ minutes: 6 }, { float: false });
          stamp('kmrt');
          travelTo('kaohsiung', MAPS.kaohsiung.spots![other]);
          window.setTimeout(() => toast(t.riding), 300);
        },
      },
      { text: t.ask, run: () => quiz(objName('kh_lrt'), '🚈', t.text, t.q, t.a, t.right, t.wrong, () => onceToday('kh.lrt') && apply({ study: 4, stars: 2 })) },
      { text: m().common.back },
    ],
  });
}

// --- Food ----------------------------------------------------------------------------------------------

/** A stall of Liuhe Night Market: covered by day, ordering in English at night. */
function nightStall(id: 'kh_oyster' | 'kh_squid', icon: string, fx: { energy?: number; mood?: number }): () => void {
  return () => {
    if (!marketOpen()) return say({ who: objName(id), icon: '⛺', text: kh().closed });
    order(id, icon, kh().food[id === 'kh_oyster' ? 'oyster' : 'squid'], fx, () => stamp('liuhe'));
  };
}

// --- Love River -----------------------------------------------------------------------------------------

function loveBoat(): void {
  const t = kh().boat;
  say({
    who: objName('kh_loveBoat'),
    icon: '⛵',
    text: t.text,
    choices: [
      {
        text: t.buy,
        cost: m().world3.market.cost(t.price),
        run: () => {
          if (S().coins < t.price) return toast(t.poor);
          quiz(objName('kh_loveBoat'), '⛵', t.text, t.q, t.a, t.right, t.wrong, () => {
            apply({ coins: -t.price, mood: 15, energy: 4, minutes: 30 });
            if (onceToday('kh.boat')) apply({ stars: 3 }, { float: false });
            celebrate();
            stamp('loveRiver');
          });
        },
      },
      { text: m().common.back },
    ],
  });
}

registerObjects({
  kh_mrtZuoying: () => kmrt('zuoying'),
  kh_mrtFormosa: () => kmrt('formosa'),
  kh_mrtYancheng: () => kmrt('yancheng'),
  kh_lrt: () => lrt('pier2'),
  kh_lrtHamasen: () => lrt('hamasen'),
  kh_lotusView() {
    const t = kh().lotus;
    landmark('kh_lotusView', '🔭', t.text, t.look, () => {
      sfx('ring');
      burst('sparkle', 5);
      if (onceToday('kh.lotus')) apply({ mood: 8, stars: 2 });
      stamp('lotusPond');
      return `${t.looked}\n\n“${t.phrase}”`;
    }, t.phrase);
  },
  kh_dragon() {
    const t = kh().dragon;
    factQuiz('kh_dragon', '🐉', t.text, t.ask, t, () => {
      burst('sparkle', 8);
      stamp('lotusPond');
    });
  },
  kh_dome() {
    const t = kh().dome;
    factQuiz('kh_dome', '🌈', t.text, t.ask, t, () => {
      celebrate();
      stamp('formosa');
    });
  },
  kh_papaya: () => order('kh_papaya', '🥤', kh().food.papaya, { energy: 12, mood: 12 }, () => stamp('liuhe')),
  kh_oyster: nightStall('kh_oyster', '🍳', { energy: 28, mood: 8 }),
  kh_squid: nightStall('kh_squid', '🦑', { energy: 22, mood: 10 }),
  kh_loveBoat: loveBoat,
  kh_pier2() {
    const t = kh().pier2;
    factQuiz('kh_pier2', '🎨', t.text, t.ask, t, () => stamp('pier2'));
  },
  kh_giants() {
    const t = kh().giants;
    landmark('kh_giants', '🗿', t.text, t.pose, () => {
      sfx('ok');
      burst('sparkle', 6);
      if (onceToday('kh.giants')) apply({ mood: 10, stars: 2 });
      stamp('pier2');
      return `${t.posed}\n\n“${t.phrase}”`;
    }, t.phrase);
  },
  kh_ferry() {
    const t = kh().ferry;
    factQuiz('kh_ferry', '⛴️', t.text, t.ask, t, () => {
      sfx('water');
      stamp('harbor');
    });
  },
  kh_icecream: () => order('kh_icecream', '🍦', kh().food.icecream, { energy: 10, mood: 14 }, () => stamp('harbor')),
  kh_tower85() {
    const t = kh().tower;
    factQuiz('kh_tower85', '🏙️', t.text, t.ask, t, () => stamp('harbor'));
  },
});

/** NPC talk: the station staff and the Dome guide ask a question; vendors and the boatman offer their thing. */
registerTalk((id) => {
  if (!id.startsWith('kh_')) return false;
  const t = kh().talk;
  if (id === 'kh_stationStaff') {
    const r = t.kh_stationStaff;
    quiz(npcName(id), '🧢', r.intro, r.q, r.a, r.right, r.wrong, () => onceToday('kh.staffQuiz') && apply({ study: 4, stars: 1 }));
    return true;
  }
  if (id === 'kh_domeGuide') {
    const r = t.kh_domeGuide;
    quiz(npcName(id), '🌈', r.intro, r.q, r.a, r.right, r.wrong, () => onceToday('kh.guideQuiz') && apply({ study: 4, stars: 1 }));
    return true;
  }
  const lines = (t as unknown as Record<string, readonly string[] | undefined>)[id];
  if (!Array.isArray(lines)) return false;
  const icons: Record<string, string> = {
    kh_monk: '🙏', kh_papayaVendor: '🥤', kh_oysterAuntie: '🍳', kh_boatman: '⛵', kh_cyclist: '🚲', kh_artist: '🎨',
    kh_fisher: '🎣', kh_surfer: '🏄', kh_kiteKid: '🪁', kh_kiteGirl: '🪁',
  };
  const line = pick(lines).replace(/^English practice:\s*/, '').replace(/\s*\(English practice\)$/, '');
  // Speak the quoted English phrases (in every language the quotes stay in English).
  const quoted = [...line.matchAll(/“([^”]+)”/g)].map((q) => q[1]).filter((q) => /^[\x20-\x7e’]+$/.test(q));
  const en = quoted.length ? quoted.join(' … ') : undefined;
  if (en) sayEnglish(en, 0.9);
  const action: Choice | null =
    id === 'kh_papayaVendor'
      ? { text: `🥤 ${objName('kh_papaya')}`, run: () => order('kh_papaya', '🥤', kh().food.papaya, { energy: 12, mood: 12 }, () => stamp('liuhe')) }
      : id === 'kh_oysterAuntie' && marketOpen()
        ? { text: `🍳 ${objName('kh_oyster')}`, run: () => order('kh_oyster', '🍳', kh().food.oyster, { energy: 28, mood: 8 }, () => stamp('liuhe')) }
        : id === 'kh_boatman'
          ? { text: `⛵ ${objName('kh_loveBoat')}`, run: loveBoat }
          : null;
  if (onceToday(`kh.talk.${id}`)) apply({ mood: 3 }, { float: false });
  say({ who: npcName(id), icon: icons[id] ?? '💬', text: line, en, choices: action ? [action, { text: m().common.back }] : undefined });
  return true;
});
